import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { LAB_CONTACT_MODE } from '../../utils/standaloneAdHocQuoteRequest';

/**
 * Lab contact mode selector and fields for a standalone custom quote send.
 *
 * @param {Object} props
 * @param {string} props.mode - {@link LAB_CONTACT_MODE}
 * @param {function(string): void} props.onModeChange
 * @param {string} props.contactName
 * @param {string} props.contactEmail
 * @param {function({ contactName?: string, contactEmail?: string }): void} props.onContactChange
 * @param {{ contact_name?: string, contact_email?: string }|null} props.globalContactSettings
 * @param {boolean} [props.disabled]
 * @param {{ contact_name?: string, contact_email?: string }} [props.fieldErrors]
 * @param {function(): void} [props.onConfigureGlobal]
 */
const LabOneTimeContactFields = ({
  mode,
  onModeChange,
  contactName,
  contactEmail,
  onContactChange,
  globalContactSettings,
  disabled = false,
  fieldErrors = {},
  onConfigureGlobal,
}) => {
  const savedName = (globalContactSettings?.contact_name || '').trim();
  const savedEmail = (globalContactSettings?.contact_email || '').trim();
  const hasSaved = Boolean(savedName && savedEmail);

  return (
    <div>
      <Form.Check
        type="radio"
        id="lab-contact-mode-saved"
        name="labContactMode"
        className="mb-2"
        label="Use saved lab contact"
        checked={mode === LAB_CONTACT_MODE.SAVED}
        disabled={disabled}
        onChange={() => onModeChange(LAB_CONTACT_MODE.SAVED)}
      />
      {mode === LAB_CONTACT_MODE.SAVED ? (
        <div className="ms-4 mb-3 small text-muted">
          {hasSaved ? (
            <span>
              {savedName} &lt;{savedEmail}&gt;
            </span>
          ) : (
            <span>
              No saved lab contact configured.{' '}
              {onConfigureGlobal ? (
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={onConfigureGlobal}
                  disabled={disabled}
                >
                  Set lab contact
                </button>
              ) : null}
            </span>
          )}
        </div>
      ) : null}

      <Form.Check
        type="radio"
        id="lab-contact-mode-one-time"
        name="labContactMode"
        className="mb-2"
        label="Use one-time lab contact for this send"
        checked={mode === LAB_CONTACT_MODE.ONE_TIME}
        disabled={disabled}
        onChange={() => onModeChange(LAB_CONTACT_MODE.ONE_TIME)}
      />
      <p className="text-muted small ms-4 mb-2">
        Used only for this quote email batch. Does not update your saved lab contact
        settings.
      </p>

      {mode === LAB_CONTACT_MODE.ONE_TIME ? (
        <Row className="g-2 ms-1">
          <Col xs={12} md={6}>
            <Form.Label className="small mb-1">Contact name</Form.Label>
            <Form.Control
              size="sm"
              value={contactName}
              onChange={e => onContactChange({ contactName: e.target.value })}
              placeholder="Lab contact name"
              disabled={disabled}
              isInvalid={Boolean(fieldErrors.contact_name)}
            />
            {fieldErrors.contact_name ? (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.contact_name}
              </Form.Control.Feedback>
            ) : null}
          </Col>
          <Col xs={12} md={6}>
            <Form.Label className="small mb-1">Contact email</Form.Label>
            <Form.Control
              size="sm"
              type="email"
              value={contactEmail}
              onChange={e => onContactChange({ contactEmail: e.target.value })}
              placeholder="lab.contact@example.com"
              disabled={disabled}
              isInvalid={Boolean(fieldErrors.contact_email)}
            />
            {fieldErrors.contact_email ? (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.contact_email}
              </Form.Control.Feedback>
            ) : null}
          </Col>
        </Row>
      ) : null}
    </div>
  );
};

export default LabOneTimeContactFields;
